/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  addDays,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import { Platform } from 'react-native';
import {
  HOTEL,
  HOTEL_SEARCH_DEFAULTS,
  HOTEL_SEARCH_URL_CHANGE_EVENT,
  HOTEL_SEARCH_QUERY_PARAMS_TEMPLATE,
  DEFAULT_DEPARTURE_OFFSET_DAYS,
  DEFAULT_STAY_DAYS,
  MAX_ADULTS_PER_ROOM,
  MAX_CHILD_AGE,
  MAX_CHILDREN_PER_ROOM,
  MAX_DATE_OFFSET_DAYS,
  MAX_HOTEL_ROOMS,
  MAX_LOS,
  MIN_RETURN_DATE_OFFSET_DAYS,
  SORT_BY,
} from '../configs/constants';
import {
  HotelRecentSearch,
  HotelFilters,
  HotelSearchData,
  HotelSearchParams,
  RoomConfig,
  Suggestions,
} from '../types';
import { buildHotelDetailsSlug } from './hotelDetailsSlug';
import { splitOccupancyByRoom } from './occupancy';
import {
  isCanonicalHotelDetailsRoutePath,
  joinHotelDetailsPath,
  normalizeWebHotelRoutePath,
} from './hotelDetailsRoute';
import type { HotelRoutePaths } from '../context/HotelsNavigationContext';
import { resolveSupportedHotelLanguageId } from './language';

type QueryParamValue = string | string[] | undefined;
type HotelQueryParams = Record<string, QueryParamValue>;

type HotelDeepLinkParams = Record<
  string,
  string | string[] | boolean | undefined
>;

type ParseHotelSearchParamsOptions = {
  defaultOccupancyWhenMissing?: boolean;
};

export type HotelDeepLinkRoute = {
  params: HotelDeepLinkParams | null;
  screenPath: string | null;
  isSearchListScreen: boolean;
  isMapViewScreen: boolean;
  isSearchWidgetScreen: boolean;
  isDetailsScreen: boolean;
  isConfirmationScreen: boolean;
};

const RESERVED_HOTELS_PATH_SEGMENTS = new Set([
  'booking-detail',
  'booking-details',
  'results',
  'result',
  'mapview',
  'search',
  'confirmation',
  'payment',
  'trip-details',
  'en',
]);

export type HotelDeeplinkRoutePaths = Required<
  Pick<
    HotelRoutePaths,
    'root' | 'search' | 'results' | 'details' | 'confirmation' | 'mapview'
  >
>;

const DEFAULT_DEEPLINK_ROUTE_PATHS: HotelDeeplinkRoutePaths = {
  root: '/',
  search: '/search',
  results: '/result',
  details: '/',
  confirmation: '/confirmation',
  mapview: '/mapview',
};

type ParsedHotelDeeplinkUrl = {
  hostname: string | null;
  query: string;
  normalizedPath: string;
};

const parseHotelDeeplinkUrl = (url: string): ParsedHotelDeeplinkUrl | null => {
  const parsedUrl = new URL(url, 'https://www.airasia.com');
  const hostname = parsedUrl.hostname || null;

  if (hostname === 'expo-development-client') {
    return null;
  }

  const isHttpUrl =
    parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  // For app-scheme deeplinks (airasia://hotel/b59d) the discriminator lives in
  // the host (`hotel`) while the id is in the path (`/b59d`). Combine both so
  // old-scheme paths like `hotel/b59d` and `hotel/search` resolve correctly.
  const rawPath = isHttpUrl
    ? parsedUrl.pathname || '/'
    : `${parsedUrl.hostname || ''}${parsedUrl.pathname || ''}` || '/';
  const normalizedPath = (rawPath || '/').replace(/^\/+|\/+$/g, '');

  return {
    hostname,
    query: parsedUrl.search,
    normalizedPath,
  };
};

const resolveDetailsExpoPath = (normalizedPath: string): string | null => {
  if (!normalizedPath) {
    return null;
  }

  // `airasia://hotel/{id}` and SEO slug:
  // `airasia://hotel/{area}/{slug}/{id}/` → `/{area}/{slug}/{id}` or `/{id}`
  const hotelPathMatch = normalizedPath.match(/^hotel\/(.+)$/);
  if (hotelPathMatch?.[1]) {
    const segments = hotelPathMatch[1].split('/').filter(Boolean);
    const hotelId = segments[segments.length - 1];
    if (
      hotelId &&
      !RESERVED_HOTELS_PATH_SEGMENTS.has(hotelId) &&
      !RESERVED_HOTELS_PATH_SEGMENTS.has(segments[0])
    ) {
      if (segments.length >= 3) {
        return `/${segments.join('/')}`;
      }

      return `/${hotelId}`;
    }
  }

  return null;
};

const isConfirmationDeeplinkPath = (normalizedPath: string): boolean =>
  normalizedPath === 'confirmation' ||
  normalizedPath === 'hotel/confirmation' ||
  normalizedPath.endsWith('/hotel/confirmation') ||
  normalizedPath === 'hotels/confirmation' ||
  normalizedPath.endsWith('/hotels/confirmation') ||
  normalizedPath === 'hotel/booking-detail' ||
  normalizedPath.endsWith('/hotel/booking-detail');

/** Public hotel entry paths that resolve to the search widget by default. */
const isHotelsRootOrSearchPath = (normalizedPath: string): boolean =>
  !normalizedPath ||
  normalizedPath === 'hotel' ||
  normalizedPath === 'hotel/search' ||
  normalizedPath === 'search';

/**
 * Web/native search-entry pathnames that should stay clean when there is no
 * destination/hotel identity in the URL (/, /search, /hotel, /hotel/search).
 */
export const isHotelSearchEntryPath = (pathname?: string | null): boolean => {
  const normalizedPath = (pathname || '/').replace(/^\/+|\/+$/g, '');
  return isHotelsRootOrSearchPath(normalizedPath);
};

const queryParamsFromSearch = (query: string): HotelQueryParams => {
  const queryParams: HotelQueryParams = {};

  new URLSearchParams(query).forEach((value, key) => {
    const currentValue = queryParams[key];

    if (currentValue === undefined) {
      queryParams[key] = value;
      return;
    }

    queryParams[key] = Array.isArray(currentValue)
      ? [...currentValue, value]
      : [currentValue, value];
  });

  return queryParams;
};

/**
 * Hotels root/search → list only when a destination/hotel identity is present.
 * Locale/currency/filter-only params should keep the search widget.
 */
export const hasHotelListIdentityCriteria = (
  queryParams: HotelQueryParams,
): boolean => {
  const listKeys = [
    'locationId',
    'location_id',
    'destination',
    'aaId',
    'user_selected_hotel_id',
    'user_selected_id',
    'selected_hotel_id',
    'selectedHotelId',
  ];

  return listKeys.some((key) => {
    const value = queryParams[key];
    return Array.isArray(value)
      ? value.some((item) => item.length > 0)
      : typeof value === 'string' && value.length > 0;
  });
};

const shouldOpenResultsFromHotelsEntry = (
  normalizedPath: string,
  queryParams: HotelQueryParams,
): boolean =>
  isHotelsRootOrSearchPath(normalizedPath) &&
  hasHotelListIdentityCriteria(queryParams);

export const buildRouteWithQueryParams = (
  routePath: string,
  queryParams: HotelQueryParams,
): string => {
  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
    } else if (value !== undefined) {
      searchParams.append(key, value);
    }
  });

  const query = searchParams.toString();

  return query ? `${routePath}?${query}` : routePath;
};

/** Search vs list when landing on hotel root (`/hotel` or package `/`). */
export const resolveHotelRootEntryPathname = (
  queryParams: HotelQueryParams,
  routePaths: Pick<HotelDeeplinkRoutePaths, 'search' | 'results'>,
): string =>
  hasHotelListIdentityCriteria(queryParams)
    ? routePaths.results
    : routePaths.search;

/**
 * Maps a super-app hotel deeplink to an Expo Router location for embedded ExpoRoot.
 */
export const mapHotelDeeplinkToExpoLocation = (
  url: string | null | undefined,
  routePaths: HotelDeeplinkRoutePaths = DEFAULT_DEEPLINK_ROUTE_PATHS,
): string | undefined => {
  if (!url) {
    return undefined;
  }

  const parsed = parseHotelDeeplinkUrl(url);
  if (!parsed) {
    return undefined;
  }

  const { normalizedPath, query } = parsed;
  const queryParams = queryParamsFromSearch(query);
  const screenPath = isHotelsRootOrSearchPath(normalizedPath)
    ? shouldOpenResultsFromHotelsEntry(normalizedPath, queryParams)
      ? 'results'
      : '/'
    : normalizeScreenPath(normalizedPath ? `/${normalizedPath}` : '/');

  let expoLocation: string | undefined;

  if (screenPath === 'results') {
    expoLocation = `${routePaths.results}${query}`;
  } else if (isConfirmationDeeplinkPath(normalizedPath)) {
    expoLocation = `${routePaths.confirmation}${query}`;
  } else {
    const detailsPath = resolveDetailsExpoPath(normalizedPath);
    if (detailsPath) {
      expoLocation = `${joinHotelDetailsPath(
        routePaths.details,
        detailsPath,
      )}${query}`;
    } else {
      expoLocation = `${routePaths.search}${query}`;
    }
  }

  if (__DEV__) {
    console.log(
      '[HotelMMB]',
      'mapHotelDeeplinkToExpoLocation',
      JSON.stringify({
        input: url,
        normalizedPath,
        screenPath,
        expoLocation,
      }),
    );
  }

  return expoLocation;
};

export type HotelSearchWidgetInitialValues = {
  destination: Suggestions | null;
  checkIn: Date;
  checkOut: Date;
  rooms: RoomConfig[];
  source: 'default' | 'recent-search' | 'deeplink';
  locationId?: string;
};

type HotelListUrlParams = {
  routePath: string;
  destination: Suggestions;
  checkIn: Date | string;
  checkOut: Date | string;
  rooms: RoomConfig[];
  currency: string;
  countryCode: string;
  languageId: string;
  userSelectedId?: string;
  category?: string[];
  payment_method?: string[];
  star_rating?: string[];
  amenity?: string[];
  rate_amenity?: string[];
  landmark?: string;
};

type DetailRouteSearchParams = Record<string, string | string[] | undefined>;

const VALID_GUEST_RATING_VALUES = new Set(['3', '3.5', '4', '4.5']);

export const normalizeGuestRatingFilterValue = (
  value: string | number | undefined | null,
): string => {
  if (value === undefined || value === null) {
    return '';
  }

  const normalized = String(value).trim().replace(/\+/g, '').replace(/\s/g, '');

  return VALID_GUEST_RATING_VALUES.has(normalized) ? normalized : '';
};

/**
 * Filter checkboxes use uppercase snake values (PAY_NOW, FREE_CANCELLATION).
 * Deeplink / URL sync writes lowercase (`pay_now`). Normalize on read so UI
 * selections match.
 */
export const normalizePaymentPolicyFilterValue = (
  value: string | undefined | null,
): string => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim().replace(/-/g, '_').toUpperCase();
};

export const normalizePaymentPolicyFilterValues = (
  values: string[] | string | undefined,
): string[] | undefined => {
  if (values == null) {
    return undefined;
  }

  const list = Array.isArray(values) ? values : [values];
  const normalized = list
    .map((value) => normalizePaymentPolicyFilterValue(value))
    .filter((value) => value.length > 0);

  return normalized.length > 0 ? normalized : undefined;
};

export const isHotelDetailsSearchRoutePath = (
  pathname?: string | null,
): boolean => {
  const normalizedPath = (pathname || '').trim();

  if (!normalizedPath) {
    return false;
  }

  return isCanonicalHotelDetailsRoutePath(
    normalizeWebHotelRoutePath(normalizedPath),
  );
};

type BuildHotelDetailsUrlOptions = {
  currentSearchParams: DetailRouteSearchParams;
  hotelId: string;
  destination: Suggestions;
  checkIn: NonNullable<HotelSearchData['checkIn']>;
  checkOut: NonNullable<HotelSearchData['checkOut']>;
  rooms: RoomConfig[];
  currency: string;
  countryCode: string;
  languageId: string;
  routePath?: string;
  useCanonicalSearchParams?: boolean;
};

const LEGACY_CHILD_MAX_AGE = MAX_CHILD_AGE;
const MIN_ADULTS_PER_ROOM = 1;
const DEFAULT_ADULTS_PER_ROOM = 2;
const HOTEL_CURRENCY_PATTERN = /^[A-Za-z]{3}$/;
const HOTEL_COUNTRY_CODE_PATTERN = /^[A-Za-z]{2}$/;

/** Adults outside 1–12 fall back to the default room occupancy. */
const sanitizeAdultsPerRoom = (adults: number): number =>
  adults >= MIN_ADULTS_PER_ROOM && adults <= MAX_ADULTS_PER_ROOM
    ? adults
    : DEFAULT_ADULTS_PER_ROOM;

/** Counts above `max` are treated as missing so callers can use the default. */
const countWithinMax = (value: number, max: number): number => {
  if (!Number.isFinite(value) || value < 1 || value > max) {
    return 0;
  }

  return Math.floor(value);
};

const toDateKey = (date: Date): string => format(date, 'yyyy-MM-dd');

export const formatDateValue = (date: Date | string): string =>
  typeof date === 'string'
    ? (() => {
        const parsedDate = parseISO(date);
        if (isValid(parsedDate)) {
          return format(parsedDate, 'yyyy-MM-dd');
        }

        return date;
      })()
    : format(date, 'yyyy-MM-dd');

const appendExistingQueryParam = (
  queryParams: URLSearchParams,
  key: string,
  value: string | string[] | undefined,
) => {
  if (key === 'slug' || value == null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      queryParams.append(key, item);
    });
    return;
  }

  queryParams.append(key, value);
};

export const getDefaultHotelSearchDates = () => {
  const checkIn = addDays(
    startOfDay(new Date()),
    DEFAULT_DEPARTURE_OFFSET_DAYS,
  );
  const checkOut = addDays(checkIn, DEFAULT_STAY_DAYS);

  return { checkIn, checkOut };
};

const HOTEL_DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const parseDateKey = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!HOTEL_DATE_KEY_PATTERN.test(trimmed)) {
    return null;
  }

  const parsedDate = parseISO(trimmed);
  if (!isValid(parsedDate)) {
    return null;
  }

  const normalized = startOfDay(parsedDate);
  if (format(normalized, 'yyyy-MM-dd') !== trimmed) {
    return null;
  }

  return normalized;
};

const isUsableHotelDate = (date: Date, today: Date, latest: Date): boolean =>
  date >= today && date <= latest;

const checkoutWithinStayLimits = (
  checkIn: Date,
  latest: Date,
  stayDays: number,
): Date | null => {
  let checkOut = addDays(checkIn, stayDays);
  if (checkOut > latest) {
    checkOut = latest;
  }

  const nights = differenceInCalendarDays(checkOut, checkIn);
  if (nights < MIN_RETURN_DATE_OFFSET_DAYS || nights > MAX_LOS) {
    return null;
  }

  return checkOut;
};

export const getDefaultHotelRooms = (): RoomConfig[] => [
  {
    id: '1',
    adults: 2,
    children: [],
  },
];

const getStringQueryParam = (
  queryParams: HotelQueryParams,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = queryParams[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  return undefined;
};

const getListQueryParam = (
  queryParams: HotelQueryParams,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = queryParams[key];

    if (typeof value === 'string' && value.length > 0) {
      return value;
    }

    if (Array.isArray(value) && value.length > 0) {
      const joinedValue = value.filter(Boolean).join(',');
      if (joinedValue.length > 0) {
        return joinedValue;
      }
    }
  }

  return undefined;
};

const parseNumericList = (value: string | undefined): number[] => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((item) => Number.parseInt(item.trim(), 10))
    .filter((item) => !Number.isNaN(item));
};

const buildChildId = (roomIndex: number, age: number) =>
  `child-${roomIndex}-${age}`;

const parseChildToken = (
  token: string,
  roomCountHint: number,
  preferRoomScopedFormat: boolean,
): { roomIndex: number; age: number } | null => {
  const [left, right] = token
    .split('_')
    .map((part) => Number.parseInt(part.trim(), 10));

  if (Number.isNaN(left) || Number.isNaN(right)) {
    return null;
  }

  if (preferRoomScopedFormat) {
    return left >= 1 && right >= 0 && right <= LEGACY_CHILD_MAX_AGE
      ? {
          age: right,
          roomIndex: left - 1,
        }
      : null;
  }

  const looksLikeLegacyFormat =
    left >= 0 &&
    left <= LEGACY_CHILD_MAX_AGE &&
    right >= 0 &&
    right < roomCountHint;

  if (looksLikeLegacyFormat) {
    return {
      age: left,
      roomIndex: right,
    };
  }

  const looksLikeDeepLinkFormat =
    left >= 1 && right >= 0 && right <= LEGACY_CHILD_MAX_AGE;

  if (looksLikeDeepLinkFormat) {
    return {
      age: right,
      roomIndex: left - 1,
    };
  }

  if (left >= 0 && left <= LEGACY_CHILD_MAX_AGE && right >= 0) {
    return {
      age: left,
      roomIndex: right,
    };
  }

  return null;
};

const buildParsedRooms = (
  adultsValue: string | undefined,
  roomsValue: string | undefined,
  childrenValue: string | undefined,
  preferRoomScopedFormat = false,
): {
  parsedRooms: RoomConfig[];
  rooms: string;
  adults: string;
  children: string;
} => {
  const parsedAdults = parseNumericList(adultsValue);
  const adultsList = parsedAdults.length > MAX_HOTEL_ROOMS ? [] : parsedAdults;
  const parsedExplicitRooms = Number.parseInt(roomsValue || '', 10);
  const explicitRooms = countWithinMax(parsedExplicitRooms, MAX_HOTEL_ROOMS);
  const childTokens = childrenValue
    ? childrenValue.split(',').filter(Boolean)
    : [];
  const roomCountHint = Math.max(explicitRooms, adultsList.length, 1);

  const parsedChildren = childTokens
    .map((token) =>
      parseChildToken(token, roomCountHint, preferRoomScopedFormat),
    )
    .filter((child): child is { roomIndex: number; age: number } => !!child);

  const inferredRoomCount = parsedChildren.reduce((maxRoomIndex, child) => {
    const nextCount = child.roomIndex + 1;
    if (nextCount > MAX_HOTEL_ROOMS) {
      return maxRoomIndex;
    }

    return Math.max(maxRoomIndex, nextCount);
  }, roomCountHint);

  const totalRooms = Math.max(inferredRoomCount, 1);
  const childCountByRoom = new Map<number, number>();
  parsedChildren.forEach((child) => {
    if (child.roomIndex < 0 || child.roomIndex >= totalRooms) {
      return;
    }

    childCountByRoom.set(
      child.roomIndex,
      (childCountByRoom.get(child.roomIndex) || 0) + 1,
    );
  });

  const parsedRooms = Array.from({ length: totalRooms }, (_, index) => ({
    id: String(index + 1),
    adults: sanitizeAdultsPerRoom(
      adultsList[index] || adultsList[0] || DEFAULT_ADULTS_PER_ROOM,
    ),
    children: [],
  })) as RoomConfig[];

  parsedChildren.forEach((child) => {
    if (child.roomIndex < 0 || child.roomIndex >= parsedRooms.length) {
      return;
    }

    if ((childCountByRoom.get(child.roomIndex) || 0) > MAX_CHILDREN_PER_ROOM) {
      return;
    }

    parsedRooms[child.roomIndex].children.push({
      id: buildChildId(child.roomIndex, child.age),
      age: child.age,
    });
  });

  const adultsOutOfRange = adultsList.some(
    (count) => count < MIN_ADULTS_PER_ROOM || count > MAX_ADULTS_PER_ROOM,
  );

  return {
    parsedRooms,
    rooms: String(totalRooms),
    adults: !adultsList.length
      ? String(DEFAULT_ADULTS_PER_ROOM)
      : adultsOutOfRange
        ? adultsList.map(sanitizeAdultsPerRoom).join(',')
        : adultsValue || String(DEFAULT_ADULTS_PER_ROOM),
    children: childrenValue || '',
  };
};

const tryDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const parseRoomsFromOccupancy = (
  occupancyValue: string | undefined,
): RoomConfig[] => {
  if (!occupancyValue) {
    return [];
  }

  const decodedOccupancy = tryDecodeURIComponent(occupancyValue);
  const roomSegments = splitOccupancyByRoom(decodedOccupancy);

  if (roomSegments.length > MAX_HOTEL_ROOMS) {
    return [];
  }

  const parsedRooms = roomSegments
    .map((segment, index) => {
      const match = segment.match(/^(\d+)(?:\(([^)]*)\))?$/);
      if (!match) {
        return null;
      }

      const adults = Number.parseInt(match[1], 10);
      if (Number.isNaN(adults) || adults <= 0) {
        return null;
      }

      const childrenAges = (match[2] || '')
        .split(',')
        .map((age) => Number.parseInt(age.trim(), 10))
        .filter(
          (age) => !Number.isNaN(age) && age >= 0 && age <= MAX_CHILD_AGE,
        );
      const boundedChildrenAges =
        childrenAges.length > MAX_CHILDREN_PER_ROOM ? [] : childrenAges;

      return {
        id: String(index + 1),
        adults: sanitizeAdultsPerRoom(adults),
        children: boundedChildrenAges.map((age, childIndex) => ({
          id: `child-${index}-${childIndex}`,
          age,
        })),
      } as RoomConfig;
    })
    .filter((room): room is RoomConfig => !!room);

  return parsedRooms;
};

/**
 * V5 sort values → V6 canonical values.
 * Legacy V5 often sends analytics list names like
 * "Hotel Search Results - Recommendation" instead of a sort key.
 * Unknown values pass through unchanged.
 *
 * Mapping:
 * - …Recommendation → recommended
 * - …Price Low to High → lowToHigh
 * - …Price High to Low → highToLow
 * - …Guest Rating → topRating
 */
export const normalizeHotelSortValue = (
  value: string | undefined | null,
): string => {
  if (value === undefined || value === null) {
    return '';
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }

  const normalized = trimmed.toLowerCase();

  if (
    normalized === SORT_BY.DEFAULT ||
    normalized === SORT_BY.PRICE_LOW_TO_HIGH ||
    normalized === SORT_BY.PRICE_HIGH_TO_LOW ||
    normalized === SORT_BY.GUEST_RATING
  ) {
    return normalized;
  }

  if (
    normalized === 'recommendation' ||
    normalized.includes('recommendation')
  ) {
    return SORT_BY.DEFAULT;
  }

  if (
    normalized.includes('price low to high') ||
    normalized === 'price_low_to_high' ||
    normalized === 'lowest price' ||
    normalized === 'lowestprice'
  ) {
    return SORT_BY.PRICE_LOW_TO_HIGH;
  }

  if (
    normalized.includes('price high to low') ||
    normalized === 'price_high_to_low' ||
    normalized === 'highest price' ||
    normalized === 'highestprice'
  ) {
    return SORT_BY.PRICE_HIGH_TO_LOW;
  }

  if (
    normalized.includes('guest rating') ||
    normalized === 'guest_rating' ||
    normalized === 'toprating' ||
    normalized.includes('top guest rating')
  ) {
    return SORT_BY.GUEST_RATING;
  }

  return trimmed;
};

const getArrayQueryParam = (
  queryParams: HotelQueryParams,
  ...keys: string[]
): string[] | undefined => {
  for (const key of keys) {
    const value = queryParams[key];

    if (Array.isArray(value) && value.length > 0) {
      const items = value.map(String).filter((item) => item.length > 0);
      if (items.length > 0) {
        return items;
      }
    }

    if (typeof value === 'string' && value.length > 0) {
      return [value];
    }
  }

  return undefined;
};

const getUniqueArrayQueryParam = (
  queryParams: HotelQueryParams,
  ...keys: string[]
): string[] | undefined => {
  const values = getArrayQueryParam(queryParams, ...keys);
  return values ? Array.from(new Set(values)) : undefined;
};

const getSanitizedDates = (queryParams: HotelQueryParams) => {
  const { checkIn: defaultCheckInDate, checkOut: defaultCheckoutDate } =
    getDefaultHotelSearchDates();
  const today = startOfDay(new Date());
  const latestDate = addDays(today, MAX_DATE_OFFSET_DAYS);
  const defaultDates = {
    checkin: toDateKey(defaultCheckInDate),
    checkout: toDateKey(defaultCheckoutDate),
  };

  // V6 keys win when both V5 and V6 aliases are present.
  const rawCheckin = getStringQueryParam(queryParams, 'checkin', 'fromDate');
  const rawCheckout = getStringQueryParam(queryParams, 'checkout', 'toDate');
  const parsedCheckin = rawCheckin ? parseDateKey(rawCheckin) : null;
  const parsedCheckout = rawCheckout ? parseDateKey(rawCheckout) : null;
  const usableCheckin =
    parsedCheckin && isUsableHotelDate(parsedCheckin, today, latestDate)
      ? parsedCheckin
      : null;
  const usableCheckout =
    parsedCheckout && isUsableHotelDate(parsedCheckout, today, latestDate)
      ? parsedCheckout
      : null;

  if (!rawCheckin && !rawCheckout) {
    return defaultDates;
  }

  if (rawCheckin && !usableCheckin) {
    return defaultDates;
  }

  if (!rawCheckin && rawCheckout) {
    if (!usableCheckout) {
      return defaultDates;
    }

    let checkIn = addDays(usableCheckout, -DEFAULT_STAY_DAYS);
    if (checkIn < today) {
      checkIn = today;
    }

    const nights = differenceInCalendarDays(usableCheckout, checkIn);
    if (
      checkIn > latestDate ||
      nights < MIN_RETURN_DATE_OFFSET_DAYS ||
      nights > MAX_LOS
    ) {
      return defaultDates;
    }

    return {
      checkin: toDateKey(checkIn),
      checkout: toDateKey(usableCheckout),
    };
  }

  const checkIn = usableCheckin as Date;

  if (!usableCheckout || usableCheckout <= checkIn) {
    const checkOut = checkoutWithinStayLimits(
      checkIn,
      latestDate,
      DEFAULT_STAY_DAYS,
    );
    if (!checkOut) {
      return defaultDates;
    }

    return {
      checkin: toDateKey(checkIn),
      checkout: toDateKey(checkOut),
    };
  }

  const nights = differenceInCalendarDays(usableCheckout, checkIn);
  if (nights <= MAX_LOS) {
    return {
      checkin: toDateKey(checkIn),
      checkout: toDateKey(usableCheckout),
    };
  }

  const shiftedCheckOut = checkoutWithinStayLimits(
    checkIn,
    latestDate,
    MAX_LOS,
  );
  if (!shiftedCheckOut) {
    return defaultDates;
  }

  return {
    checkin: toDateKey(checkIn),
    checkout: toDateKey(shiftedCheckOut),
  };
};

export const mapRecentSearchToSuggestions = (
  recentSearch: HotelRecentSearch,
): Suggestions => {
  const { destination } = recentSearch;
  const isHotelDestination = destination.type?.toLowerCase() === HOTEL;

  return {
    ...destination,
    aa_id: isHotelDestination ? destination.aa_id || '' : '',
  };
};

const mapRecentSearchToRooms = (
  recentSearch: HotelRecentSearch,
): RoomConfig[] => {
  if (recentSearch.roomConfigs?.length) {
    if (recentSearch.roomConfigs.length > MAX_HOTEL_ROOMS) {
      return getDefaultHotelRooms();
    }

    return recentSearch.roomConfigs.map((room) => ({
      ...room,
      children:
        room.children.length > MAX_CHILDREN_PER_ROOM ? [] : room.children,
    }));
  }

  if (
    !recentSearch.rooms.length ||
    recentSearch.rooms.length > MAX_HOTEL_ROOMS
  ) {
    return getDefaultHotelRooms();
  }

  return recentSearch.rooms.map((room, index) => ({
    id: String(index + 1),
    adults: room.adult,
    children:
      room.child > MAX_CHILDREN_PER_ROOM
        ? []
        : Array.from({ length: Math.max(0, room.child) }, (_, childIndex) => ({
            id: `child-${index}-${childIndex}`,
            age: LEGACY_CHILD_MAX_AGE,
          })),
  }));
};

const buildDeepLinkDestination = (
  locationId: string | undefined,
  aaId?: string,
  destinationName?: string,
  destinationType?: string,
): Suggestions | null => {
  if (!locationId) {
    return null;
  }

  const resolvedDestinationName = destinationName || '';

  return {
    aa_id: aaId || '',
    city_id: null,
    coordinates: {
      lat: 0,
      lon: 0,
    },
    country_code: '',
    full_name: resolvedDestinationName,
    id: locationId,
    name: resolvedDestinationName,
    type: destinationType || (aaId ? 'hotel' : 'city'),
  };
};

const normalizeScreenPath = (rawPath: string | null | undefined): string => {
  const normalizedPath = (rawPath || '/').replace(/^\/+|\/+$/g, '');

  if (!normalizedPath) {
    return '/';
  }

  if (
    normalizedPath.endsWith('/search') ||
    normalizedPath === 'search' ||
    normalizedPath.endsWith('/result') ||
    normalizedPath === 'result'
  ) {
    return 'results';
  }

  return '/';
};

export const hasHotelSearchCriteria = (
  queryParams: HotelQueryParams,
): boolean => {
  const searchKeys = [
    'locationId',
    'location_id',
    'destination',
    'checkin',
    'fromDate',
    'checkout',
    'toDate',
    'adults',
    'rooms',
    'children',
    'occupancy',
    'aaId',
    'user_selected_hotel_id',
    'user_selected_id',
    'selected_hotel_id',
    'selectedHotelId',
    'guestRating',
    'F_R',
    'currency',
    'countryCode',
    'country_code',
    'languageId',
    'applyBigpoints',
    'sort',
    'sortOption',
    'star_rating',
    'hotelClass',
    'amenity',
    'category',
    'propertyType',
    'payment_method',
    'paymentType',
    'landmark',
    'rate_amenity',
  ];

  for (const key of searchKeys) {
    const value = queryParams[key];
    if (typeof value === 'string' && value.length > 0) {
      return true;
    }
    if (Array.isArray(value) && value.length > 0) {
      return true;
    }
  }

  return false;
};

export const formatOccupancy = (rooms: RoomConfig[]): string => {
  return rooms
    .map((room) => {
      const childrenAges = room.children.map((child) => child.age).join(',');
      return room.children.length > 0
        ? `${room.adults}(${childrenAges})`
        : `${room.adults}`;
    })
    .join(',');
};

export const buildHotelListUrl = ({
  routePath,
  destination,
  checkIn,
  checkOut,
  rooms,
  currency,
  countryCode,
  languageId,
  userSelectedId,
  category,
  payment_method,
  star_rating,
  amenity,
  landmark,
}: HotelListUrlParams): string => {
  const isHotelDestination = destination.type?.toLowerCase() === HOTEL;
  const selectedDestinationHotelId = isHotelDestination
    ? destination.aa_id || ''
    : '';
  const formattedCheckIn = formatDateValue(checkIn);
  const formattedCheckOut = formatDateValue(checkOut);
  const adults = rooms.map((room) => room.adults.toString()).join(',');
  const children = rooms
    .flatMap((room, roomIndex) =>
      (room.children || []).map((child) => `${roomIndex + 1}_${child.age}`),
    )
    .join(',');
  const days = Math.max(
    differenceInCalendarDays(
      new Date(formattedCheckOut),
      new Date(formattedCheckIn),
    ),
    1,
  );

  const queryParamsObject = {
    ...HOTEL_SEARCH_QUERY_PARAMS_TEMPLATE,
    aaId: selectedDestinationHotelId,
    destination: destination.id,
    rooms: rooms.length.toString(),
    adults,
    occupancy: formatOccupancy(rooms),
    fromDate: formattedCheckIn,
    toDate: formattedCheckOut,
    days: days.toString(),
    currency,
    countryCode,
    languageId,
    ...(userSelectedId && { user_selected_id: userSelectedId }),
  };

  const queryParams = new URLSearchParams(queryParamsObject);
  if (children) {
    queryParams.append('children', children);
  }
  if (category && Array.isArray(category)) {
    category.forEach((c) => queryParams.append('category', c));
  }
  if (payment_method && Array.isArray(payment_method)) {
    payment_method.forEach((pm) => queryParams.append('payment_method', pm));
  }
  if (star_rating && Array.isArray(star_rating)) {
    star_rating.forEach((sr) => queryParams.append('star_rating', sr));
  }
  if (amenity && Array.isArray(amenity)) {
    amenity.forEach((a) => queryParams.append('amenity', a));
  }
  if (landmark) {
    queryParams.append('landmark', landmark);
  }
  return `${routePath}?${queryParams.toString()}`;
};

export const buildHotelDetailsUrl = ({
  currentSearchParams,
  hotelId,
  destination,
  checkIn,
  checkOut,
  rooms,
  currency,
  countryCode,
  languageId,
  routePath,
  useCanonicalSearchParams,
}: BuildHotelDetailsUrlOptions) => {
  const nextHotelId = destination.aa_id || hotelId;
  const nextLocationId = destination.id || '';
  const formattedCheckIn = formatDateValue(checkIn);
  const formattedCheckOut = formatDateValue(checkOut);
  const adults = rooms.map((room) => room.adults.toString()).join(',');
  const children = rooms
    .flatMap((room, roomIndex) =>
      (room.children || []).map((child) => `${roomIndex + 1}_${child.age}`),
    )
    .join(',');
  const occupancy = formatOccupancy(rooms);
  const days = String(
    Math.max(
      differenceInCalendarDays(
        new Date(formattedCheckOut),
        new Date(formattedCheckIn),
      ),
      1,
    ),
  );

  const nextRoutePath =
    routePath ||
    buildHotelDetailsSlug(nextHotelId, destination.full_name, destination.name);

  if (useCanonicalSearchParams) {
    const queryParams = new URLSearchParams();

    queryParams.set('currency', currency);
    queryParams.set('country_code', countryCode);
    queryParams.set('checkin', formattedCheckIn);
    queryParams.set('checkout', formattedCheckOut);
    queryParams.set('occupancy', occupancy);
    queryParams.set('location_id', nextLocationId);
    queryParams.set('languageId', languageId);
    queryParams.set(
      'sort',
      typeof currentSearchParams.sort === 'string'
        ? currentSearchParams.sort
        : SORT_BY.DEFAULT,
    );

    return `${nextRoutePath}?${queryParams.toString()}`;
  }

  const queryParams = new URLSearchParams();
  Object.entries(currentSearchParams).forEach(([key, value]) => {
    appendExistingQueryParam(queryParams, key, value);
  });

  queryParams.set('destination', nextLocationId);
  queryParams.set('locationId', nextLocationId);
  queryParams.set('location_id', nextLocationId);

  queryParams.set('aaId', nextHotelId);
  queryParams.set('user_selected_hotel_id', nextHotelId);
  queryParams.set('user_selected_id', nextHotelId);

  queryParams.set('fromDate', formattedCheckIn);
  queryParams.set('toDate', formattedCheckOut);
  queryParams.set('checkin', formattedCheckIn);
  queryParams.set('checkout', formattedCheckOut);

  queryParams.set('rooms', String(rooms.length));
  queryParams.set('adults', adults);
  queryParams.set('children', children);
  queryParams.set('occupancy', occupancy);
  queryParams.set('days', days);

  queryParams.set('currency', currency);
  queryParams.set('countryCode', countryCode);
  queryParams.set('country_code', countryCode);
  queryParams.delete('language');
  queryParams.delete('languageId');
  queryParams.set('languageId', languageId);

  const queryString = queryParams.toString();

  return queryString ? `${nextRoutePath}?${queryString}` : nextRoutePath;
};

export const buildHotelListUrlFromRecentSearch = ({
  routePath,
  recentSearch,
  currency,
  countryCode,
  languageId,
}: {
  routePath: string;
  recentSearch: HotelRecentSearch;
  currency: string;
  countryCode: string;
  languageId: string;
}): string => {
  const defaultDates = getDefaultHotelSearchDates();
  const hasCompleteRecentSearchDates =
    !!recentSearch.date.checkin && !!recentSearch.date.checkout;
  const checkIn = hasCompleteRecentSearchDates
    ? recentSearch.date.checkin
    : defaultDates.checkIn;
  const checkOut = hasCompleteRecentSearchDates
    ? recentSearch.date.checkout
    : defaultDates.checkOut;
  const destination = mapRecentSearchToSuggestions(recentSearch);

  const listPageUrl = buildHotelListUrl({
    routePath,
    destination,
    checkIn,
    checkOut,
    rooms: mapRecentSearchToRooms(recentSearch),
    currency,
    countryCode,
    languageId,
    userSelectedId:
      recentSearch.destination.type?.toLowerCase() === HOTEL
        ? recentSearch.destination.aa_id || undefined
        : undefined,
  });

  // Native initialParams (pinned from a hotel-list deeplink on the Hotels.tsx
  // embed path) are merged into every results entry. Append the canonical
  // keys so the recent-search destination wins; otherwise a stale deep-link
  // location_id/checkin/checkout survives the merge and
  // parseHotelSearchParams (which prefers location_id over destination)
  // re-fetches the deeplink location instead of this recent search.
  const canonicalSearchParams = new URLSearchParams({
    location_id: destination.id,
    checkin: formatDateValue(checkIn),
    checkout: formatDateValue(checkOut),
    country_code: countryCode,
  });

  return `${listPageUrl}&${canonicalSearchParams.toString()}`;
};

export const buildNewWebHotelListUrlFromRecentSearch = ({
  routePath,
  recentSearch,
  currency,
  countryCode,
  languageId,
}: {
  routePath: string;
  recentSearch: HotelRecentSearch;
  currency: string;
  countryCode: string;
  languageId: string;
}): string => {
  const defaultDates = getDefaultHotelSearchDates();
  const hasCompleteRecentSearchDates =
    !!recentSearch.date.checkin && !!recentSearch.date.checkout;
  const checkIn = hasCompleteRecentSearchDates
    ? recentSearch.date.checkin
    : defaultDates.checkIn;
  const checkOut = hasCompleteRecentSearchDates
    ? recentSearch.date.checkout
    : defaultDates.checkOut;
  const destination = mapRecentSearchToSuggestions(recentSearch);
  const selectedDestinationHotelId =
    destination.type?.toLowerCase() === HOTEL ? destination.aa_id || '' : '';
  const queryParams = new URLSearchParams({
    currency,
    country_code: countryCode,
    checkin: formatDateValue(checkIn),
    checkout: formatDateValue(checkOut),
    occupancy: formatOccupancy(mapRecentSearchToRooms(recentSearch)),
    location_id: destination.id,
    languageId,
    sort: SORT_BY.DEFAULT,
  });
  if (selectedDestinationHotelId) {
    queryParams.set('aaId', selectedDestinationHotelId);
  }

  return `${routePath}?${queryParams.toString()}`;
};

export const parseHotelSearchParams = (
  queryParams: HotelQueryParams,
  userCurrency?: string,
  options?: ParseHotelSearchParamsOptions,
): HotelSearchParams => {
  const { checkin, checkout } = getSanitizedDates(queryParams);
  const locationId =
    getStringQueryParam(
      queryParams,
      'location_id',
      'destination',
      'locationId',
    ) || HOTEL_SEARCH_DEFAULTS.LOCATION_ID;
  const rawCountryCode = getStringQueryParam(
    queryParams,
    'country_code',
    'countryCode',
  );
  const countryCode =
    rawCountryCode && HOTEL_COUNTRY_CODE_PATTERN.test(rawCountryCode.trim())
      ? rawCountryCode.trim().toUpperCase()
      : HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE;
  const occupancyValue = getListQueryParam(queryParams, 'occupancy');
  const adultsValue = getListQueryParam(queryParams, 'adults');
  const childrenValue = getListQueryParam(queryParams, 'children');
  const roomsValue = getListQueryParam(queryParams, 'rooms');
  // Prefer occupancy when present. If missing but adults exists, derive from
  // adults/rooms/children. Only fall back to default occupancy when both are missing.
  const effectiveOccupancyValue =
    occupancyValue ||
    (options?.defaultOccupancyWhenMissing && !adultsValue
      ? HOTEL_SEARCH_DEFAULTS.OCCUPANCY
      : undefined);
  const prefersRoomScopedChildrenFormat = Boolean(
    getStringQueryParam(queryParams, 'locationId', 'checkin', 'checkout'),
  );
  const roomParams = buildParsedRooms(
    adultsValue,
    roomsValue,
    childrenValue,
    prefersRoomScopedChildrenFormat,
  );

  const occupancyRooms = parseRoomsFromOccupancy(effectiveOccupancyValue);
  const hasOccupancyRooms = occupancyRooms.length > 0;
  const parsedRooms = hasOccupancyRooms
    ? occupancyRooms
    : roomParams.parsedRooms;
  const rooms = hasOccupancyRooms
    ? String(parsedRooms.length || 1)
    : roomParams.rooms;
  const adults = hasOccupancyRooms
    ? parsedRooms.map((room) => room.adults).join(',') ||
      String(DEFAULT_ADULTS_PER_ROOM)
    : roomParams.adults;
  const children = hasOccupancyRooms
    ? parsedRooms
        .flatMap((room, roomIndex) =>
          room.children.map((child) => `${roomIndex + 1}_${child.age}`),
        )
        .join(',')
    : roomParams.children;
  const aaIdValue =
    getStringQueryParam(
      queryParams,
      'aaId',
      'user_selected_hotel_id',
      'user_selected_id',
    ) || '';
  const selectedHotelIdValue =
    getStringQueryParam(queryParams, 'selected_hotel_id', 'selectedHotelId') ||
    '';
  const guestRating = normalizeGuestRatingFilterValue(
    getStringQueryParam(queryParams, 'guestRating', 'F_R'),
  );
  const debugParam = getStringQueryParam(queryParams, 'debug');
  const normalizedSort =
    normalizeHotelSortValue(
      getStringQueryParam(queryParams, 'sort', 'sortOption'),
    ) || SORT_BY.DEFAULT;
  const rawCurrency = getStringQueryParam(queryParams, 'currency');
  const currency =
    rawCurrency && HOTEL_CURRENCY_PATTERN.test(rawCurrency.trim())
      ? rawCurrency.trim().toUpperCase()
      : userCurrency || HOTEL_SEARCH_DEFAULTS.CURRENCY;

  return {
    location_id: locationId,
    checkin,
    checkout,
    adults,
    rooms,
    children,
    currency,
    country_code: countryCode,
    countryCode,
    F_R: getStringQueryParam(queryParams, 'F_R') || '',
    aaId: aaIdValue,
    user_selected_hotel_id: aaIdValue || undefined,
    selected_hotel_id: selectedHotelIdValue || undefined,
    guestRating,
    applyBigpoints:
      getStringQueryParam(queryParams, 'applyBigpoints') || 'false',
    languageId:
      resolveSupportedHotelLanguageId(
        getStringQueryParam(queryParams, 'languageId', 'language'),
      ) || 'en-gb',
    sort: normalizedSort,
    parsedRooms,
    occupancy: formatOccupancy(parsedRooms),
    // V6 keys win; fall back to V5 aliases when V6 is absent.
    category: getArrayQueryParam(queryParams, 'category', 'propertyType'),
    payment_method: normalizePaymentPolicyFilterValues(
      getArrayQueryParam(queryParams, 'payment_method', 'paymentType'),
    ),
    star_rating: getArrayQueryParam(queryParams, 'star_rating', 'hotelClass'),
    amenity: getArrayQueryParam(queryParams, 'amenity'),
    rate_amenity: getUniqueArrayQueryParam(queryParams, 'rate_amenity'),
    landmark: getStringQueryParam(queryParams, 'landmark'),
    debug: debugParam === undefined ? undefined : debugParam === 'true',
    channel: getArrayQueryParam(queryParams, 'channel'),
    mft: getStringQueryParam(queryParams, 'mft'),
  };
};

const queryParamsFromSearchParams = (
  searchParams: URLSearchParams,
): HotelQueryParams => {
  const params: HotelQueryParams = {};

  searchParams.forEach((value, key) => {
    const current = params[key];
    if (current === undefined) {
      params[key] = value;
      return;
    }

    params[key] = Array.isArray(current)
      ? [...current, value]
      : [current, value];
  });

  return params;
};

const childrenQueryFromRooms = (rooms: RoomConfig[]): string =>
  rooms
    .flatMap((room, roomIndex) =>
      room.children.map((child) => `${roomIndex + 1}_${child.age}`),
    )
    .join(',');

const occupancyRoomExceedsChildMax = (segment: string): boolean => {
  const match = segment.match(/^\d+\(([^)]*)\)$/);
  if (!match) {
    return false;
  }

  const ages = match[1]
    .split(',')
    .map((age) => age.trim())
    .filter(Boolean);

  return ages.length > MAX_CHILDREN_PER_ROOM;
};

const occupancyRoomExceedsGuestLimits = (segment: string): boolean => {
  const match = segment.match(/^(\d+)(?:\(([^)]*)\))?$/);
  if (!match) {
    return false;
  }

  const adults = Number.parseInt(match[1], 10);
  if (
    !Number.isFinite(adults) ||
    adults < MIN_ADULTS_PER_ROOM ||
    adults > MAX_ADULTS_PER_ROOM
  ) {
    return true;
  }

  if (!match[2]) {
    return false;
  }

  return match[2]
    .split(',')
    .map((age) => age.trim())
    .filter(Boolean)
    .some((ageValue) => {
      const age = Number.parseInt(ageValue, 10);
      return Number.isNaN(age) || age < 0 || age > MAX_CHILD_AGE;
    });
};

const adultsQueryExceedsPerRoomMax = (adultsValue: string): boolean => {
  const adultParts = adultsValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  if (adultParts.length > MAX_HOTEL_ROOMS) {
    return true;
  }

  return adultParts.some((part) => {
    const adults = Number.parseInt(part, 10);
    return (
      !Number.isFinite(adults) ||
      adults < MIN_ADULTS_PER_ROOM ||
      adults > MAX_ADULTS_PER_ROOM
    );
  });
};

const childrenQueryExceedsRoomLimits = (
  childrenValue: string,
  preferRoomScopedFormat: boolean,
): boolean => {
  const countsByRoom = new Map<number, number>();

  return childrenValue.split(',').some((token) => {
    const parsedChild = parseChildToken(
      token,
      MAX_HOTEL_ROOMS,
      preferRoomScopedFormat,
    );
    if (!parsedChild || parsedChild.roomIndex + 1 > MAX_HOTEL_ROOMS) {
      return !!parsedChild;
    }

    const nextCount = (countsByRoom.get(parsedChild.roomIndex) || 0) + 1;
    countsByRoom.set(parsedChild.roomIndex, nextCount);

    return nextCount > MAX_CHILDREN_PER_ROOM;
  });
};

/**
 * Rewrites room query params that exceed the guest-picker max to the parsed
 * default. Values already inside the max are left as written.
 */
export const sanitizeOutOfRangeRoomSearchParams = (
  searchParams: URLSearchParams,
): boolean => {
  const parsed = parseHotelSearchParams(
    queryParamsFromSearchParams(searchParams),
  );
  let changed = false;

  const roomsValue = searchParams.get('rooms');
  if (roomsValue) {
    const roomCount = Number.parseInt(roomsValue, 10);
    if (
      Number.isFinite(roomCount) &&
      roomCount > MAX_HOTEL_ROOMS &&
      roomsValue !== parsed.rooms
    ) {
      searchParams.set('rooms', parsed.rooms);
      changed = true;
    }
  }

  const adultsValue = searchParams.get('adults');
  if (
    adultsValue &&
    adultsQueryExceedsPerRoomMax(adultsValue) &&
    adultsValue !== parsed.adults
  ) {
    searchParams.set('adults', parsed.adults);
    changed = true;
  }

  const occupancyValue = searchParams.get('occupancy');
  if (occupancyValue) {
    const segments = splitOccupancyByRoom(
      tryDecodeURIComponent(occupancyValue),
    );
    const occupancyExceedsLimit =
      segments.length > MAX_HOTEL_ROOMS ||
      segments.some(occupancyRoomExceedsChildMax) ||
      segments.some(occupancyRoomExceedsGuestLimits);
    if (occupancyExceedsLimit && occupancyValue !== parsed.occupancy) {
      searchParams.set('occupancy', parsed.occupancy);
      changed = true;
    }
  }

  const childrenValue = searchParams.get('children');
  const prefersRoomScopedChildrenFormat = Boolean(
    searchParams.get('locationId') ||
      searchParams.get('checkin') ||
      searchParams.get('checkout'),
  );
  if (
    childrenValue &&
    childrenQueryExceedsRoomLimits(
      childrenValue,
      prefersRoomScopedChildrenFormat,
    )
  ) {
    const sanitizedChildren = childrenQueryFromRooms(parsed.parsedRooms);
    if (sanitizedChildren) {
      if (childrenValue !== sanitizedChildren) {
        searchParams.set('children', sanitizedChildren);
        changed = true;
      }
    } else {
      searchParams.delete('children');
      changed = true;
    }
  }

  const currencyValue = searchParams.get('currency');
  if (
    currencyValue &&
    currencyValue !== (parsed.currency ?? HOTEL_SEARCH_DEFAULTS.CURRENCY)
  ) {
    searchParams.set(
      'currency',
      parsed.currency ?? HOTEL_SEARCH_DEFAULTS.CURRENCY,
    );
    changed = true;
  }

  const countryCodeValue = searchParams.get('countryCode');
  if (
    countryCodeValue &&
    countryCodeValue !==
      (parsed.country_code ?? HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE)
  ) {
    searchParams.set(
      'countryCode',
      parsed.country_code ?? HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE,
    );
    changed = true;
  }

  const country_codeValue = searchParams.get('country_code');
  if (
    country_codeValue &&
    country_codeValue !==
      (parsed.country_code ?? HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE)
  ) {
    searchParams.set(
      'country_code',
      parsed.country_code ?? HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE,
    );
    changed = true;
  }

  // Keep unsupported languageId/language in the same rewrite pass as rooms and
  // dates so a later router.replace cannot put the bad value back.
  const sanitizedLanguageId = parsed.languageId || 'en-gb';
  const languageIdValue = searchParams.get('languageId');
  if (languageIdValue && languageIdValue !== sanitizedLanguageId) {
    searchParams.set('languageId', sanitizedLanguageId);
    changed = true;
  }

  const languageValue = searchParams.get('language');
  if (languageValue && languageValue !== sanitizedLanguageId) {
    searchParams.set('language', sanitizedLanguageId);
    changed = true;
  }

  return changed;
};

const replaceSearchParamWhenDifferent = (
  searchParams: URLSearchParams,
  key: string,
  value: string,
): boolean => {
  if (searchParams.get(key) === value) {
    return false;
  }

  searchParams.set(key, value);
  return true;
};

/**
 * Rewrites date query params that fail the hotel calendar rules.
 * A URL with no date params is left unchanged. A one-sided date gains its
 * partner in the same alias family.
 */
export const sanitizeHotelDateSearchParams = (
  searchParams: URLSearchParams,
): boolean => {
  const hasCheckin = searchParams.has('checkin');
  const hasFromDate = searchParams.has('fromDate');
  const hasCheckout = searchParams.has('checkout');
  const hasToDate = searchParams.has('toDate');
  const hasCheckinSide = hasCheckin || hasFromDate;
  const hasCheckoutSide = hasCheckout || hasToDate;

  if (!hasCheckinSide && !hasCheckoutSide) {
    return false;
  }

  const parsed = parseHotelSearchParams(
    queryParamsFromSearchParams(searchParams),
  );
  let changed = false;

  if (hasCheckin) {
    changed =
      replaceSearchParamWhenDifferent(
        searchParams,
        'checkin',
        parsed.checkin,
      ) || changed;
  }
  if (hasFromDate) {
    changed =
      replaceSearchParamWhenDifferent(
        searchParams,
        'fromDate',
        parsed.checkin,
      ) || changed;
  }
  if (hasCheckout) {
    changed =
      replaceSearchParamWhenDifferent(
        searchParams,
        'checkout',
        parsed.checkout,
      ) || changed;
  }
  if (hasToDate) {
    changed =
      replaceSearchParamWhenDifferent(
        searchParams,
        'toDate',
        parsed.checkout,
      ) || changed;
  }

  if (hasCheckinSide && !hasCheckoutSide) {
    const checkoutKey = hasFromDate && !hasCheckin ? 'toDate' : 'checkout';
    changed =
      replaceSearchParamWhenDifferent(
        searchParams,
        checkoutKey,
        parsed.checkout,
      ) || changed;
  }

  if (hasCheckoutSide && !hasCheckinSide) {
    const checkinKey = hasToDate && !hasCheckout ? 'fromDate' : 'checkin';
    changed =
      replaceSearchParamWhenDifferent(
        searchParams,
        checkinKey,
        parsed.checkin,
      ) || changed;
  }

  return changed;
};

export const syncOutOfRangeRoomSearchParams = (
  replaceUrl?: (pathAndQuery: string) => void,
): void => {
  if (
    Platform.OS !== 'web' ||
    typeof window === 'undefined' ||
    !window.location?.href
  ) {
    return;
  }

  const nextUrl = new URL(window.location.href);
  const roomsChanged = sanitizeOutOfRangeRoomSearchParams(nextUrl.searchParams);
  const datesChanged = sanitizeHotelDateSearchParams(nextUrl.searchParams);
  if (!roomsChanged && !datesChanged) {
    return;
  }

  window.history.replaceState(window.history.state, '', nextUrl.toString());
  replaceUrl?.(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);

  if (
    typeof window.dispatchEvent === 'function' &&
    typeof Event === 'function'
  ) {
    window.dispatchEvent(new Event(HOTEL_SEARCH_URL_CHANGE_EVENT));
  }
};

export const getCurrentWindowURLParams = (): HotelQueryParams => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return {};
  }

  try {
    const search =
      typeof window.location?.search === 'string' ? window.location.search : '';
    const searchParams = new URLSearchParams(search);
    const params: HotelQueryParams = {};

    for (const [key, value] of searchParams.entries()) {
      // Handle multiple values for the same key
      if (params[key]) {
        if (Array.isArray(params[key])) {
          params[key].push(value);
        } else {
          params[key] = [params[key] as string, value];
        }
      } else {
        params[key] = value;
      }
    }

    return params;
  } catch (error) {
    console.warn('Failed to parse window URL parameters:', error);
    return {};
  }
};

const isNumericFilterValue = (value: string) => /^\d+$/.test(value);

const mapStarRatingValues = (ratings: number[]) => {
  const mapping: Record<number, string> = {
    1: 'one',
    2: 'two',
    3: 'three',
    4: 'four',
    5: 'five',
  };

  return ratings.map((rating) => mapping[rating]).filter(Boolean);
};

export const syncHotelFiltersWithCurrentUrl = (
  filters: HotelFilters,
  replaceUrl?: (url: string) => void,
  options?: { preserveUserSelectedHotelId?: boolean },
) => {
  if (
    Platform.OS !== 'web' ||
    typeof window === 'undefined' ||
    !window.location?.href
  ) {
    return;
  }

  // Search-entry paths should stay clean on first load. If there is no real
  // hotel search criteria in the URL, do not append default params.
  if (isHotelSearchEntryPath(window.location.pathname)) {
    const currentParams = getCurrentWindowURLParams();
    if (!hasHotelSearchCriteria(currentParams)) {
      return;
    }
  }

  const currentWindowParams = getCurrentWindowURLParams();
  const deeplinkSearchParams = parseHotelSearchParams(currentWindowParams);
  const nextUrl = new URL(window.location.href);
  const queryKeys = [
    'location_id',
    'locationId',
    'destination',
    'checkin',
    'fromDate',
    'checkout',
    'toDate',
    'adults',
    'rooms',
    'children',
    'currency',
    'countryCode',
    'country_code',
    'language',
    'languageId',
    'hotels_size',
    'occupancy',
    'aaId',
    'user_selected_hotel_id',
    'user_selected_id',
    'guestRating',
    'F_R',
    'category',
    'propertyType',
    'payment_method',
    'paymentType',
    'star_rating',
    'hotelClass',
    'amenity',
    'rate_amenity',
    'landmark',
    'sort',
    'sortOption',
    'isCrossSell',
    'isFlyerDiscountEnabled',
  ] as const;

  queryKeys.forEach((key) => {
    nextUrl.searchParams.delete(key);
  });

  nextUrl.searchParams.set(
    'currency',
    deeplinkSearchParams.currency || HOTEL_SEARCH_DEFAULTS.CURRENCY,
  );
  nextUrl.searchParams.set(
    'country_code',
    deeplinkSearchParams.country_code || HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE,
  );
  nextUrl.searchParams.set('checkin', deeplinkSearchParams.checkin);
  nextUrl.searchParams.set('checkout', deeplinkSearchParams.checkout);
  nextUrl.searchParams.set(
    'occupancy',
    deeplinkSearchParams.occupancy || HOTEL_SEARCH_DEFAULTS.OCCUPANCY,
  );
  nextUrl.searchParams.set(
    'location_id',
    deeplinkSearchParams.location_id || HOTEL_SEARCH_DEFAULTS.LOCATION_ID,
  );
  nextUrl.searchParams.set(
    'languageId',
    (typeof currentWindowParams.languageId === 'string' &&
    currentWindowParams.languageId.length > 0
      ? currentWindowParams.languageId
      : deeplinkSearchParams.languageId) || 'en-gb',
  );

  if (filters.selectedHotelId) {
    nextUrl.searchParams.set('aaId', filters.selectedHotelId);
  } else if (
    options?.preserveUserSelectedHotelId &&
    deeplinkSearchParams.user_selected_hotel_id
  ) {
    nextUrl.searchParams.set(
      'aaId',
      deeplinkSearchParams.user_selected_hotel_id,
    );
  }

  const nextGuestRating = normalizeGuestRatingFilterValue(filters.guestRating);
  if (nextGuestRating) {
    nextUrl.searchParams.set('guestRating', nextGuestRating);
  }

  filters.selectedPropertyTypes
    .filter(isNumericFilterValue)
    .forEach((value) => {
      nextUrl.searchParams.append('category', value);
    });

  filters.selectedPaymentPolicies.forEach((value) => {
    nextUrl.searchParams.append('payment_method', value.toLowerCase());
  });

  mapStarRatingValues(filters.starRating).forEach((value) => {
    nextUrl.searchParams.append('star_rating', value);
  });

  filters.selectedAmenities.forEach((value) => {
    nextUrl.searchParams.append('amenity', value);
  });

  filters.selectedRateAmenities?.forEach((value) => {
    nextUrl.searchParams.append('rate_amenity', value);
  });

  if (filters.selectedLandmark) {
    nextUrl.searchParams.set('landmark', filters.selectedLandmark);
  }

  if (filters.isFlyerDiscountEnabled) {
    nextUrl.searchParams.set('isFlyerDiscountEnabled', 'true');
    nextUrl.searchParams.set('isCrossSell', 'true');
  }

  // Always include `sort` in the URL — GTM reads this param via a custom JS
  // variable and calls .toString() on it. If absent, GTM gets undefined → crash.
  nextUrl.searchParams.set(
    'sort',
    normalizeHotelSortValue(filters.sortBy) || SORT_BY.DEFAULT,
  );

  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const updatedUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

  if (updatedUrl !== currentUrl) {
    if (replaceUrl) {
      // Use requestAnimationFrame or setTimeout to defer navigation
      // until the next tick, ensuring the Root Layout is mounted.
      requestAnimationFrame(() => {
        try {
          replaceUrl(updatedUrl);
        } catch (e) {
          console.warn('Deferred navigation failed:', e);
        }
      });
      return;
    }

    window.history.replaceState(window.history.state, '', updatedUrl);
    if (
      typeof window.dispatchEvent === 'function' &&
      typeof Event === 'function'
    ) {
      window.dispatchEvent(new Event(HOTEL_SEARCH_URL_CHANGE_EVENT));
    }
  }
};

export const getHotelSearchWidgetInitialValues = ({
  queryParams,
  recentSearch,
  destinationName,
  destinationType,
  defaultOccupancyWhenMissing,
}: {
  queryParams: HotelQueryParams;
  recentSearch?: HotelRecentSearch;
  destinationName?: string;
  destinationType?: string;
  defaultOccupancyWhenMissing?: boolean;
}): HotelSearchWidgetInitialValues => {
  const defaults = getDefaultHotelSearchDates();

  if (hasHotelSearchCriteria(queryParams)) {
    const parsedParams = parseHotelSearchParams(queryParams, undefined, {
      defaultOccupancyWhenMissing,
    });
    const defaultDates = getDefaultHotelSearchDates();
    const parsedCheckInDate =
      parseDateKey(parsedParams.checkin) || defaultDates.checkIn;
    const parsedCheckOutDate =
      parseDateKey(parsedParams.checkout) || defaultDates.checkOut;

    return {
      destination: buildDeepLinkDestination(
        parsedParams.location_id,
        parsedParams.aaId || parsedParams.user_selected_hotel_id,
        destinationName,
        destinationType,
      ),
      checkIn: parsedCheckInDate,
      checkOut:
        parsedCheckOutDate > parsedCheckInDate
          ? parsedCheckOutDate
          : addDays(parsedCheckInDate, 2),
      rooms: parsedParams.parsedRooms,
      source: 'deeplink',
      locationId: parsedParams.location_id,
    };
  }

  if (recentSearch) {
    return {
      destination: mapRecentSearchToSuggestions(recentSearch),
      checkIn: recentSearch.date.checkin
        ? new Date(recentSearch.date.checkin)
        : defaults.checkIn,
      checkOut: recentSearch.date.checkout
        ? new Date(recentSearch.date.checkout)
        : defaults.checkOut,
      rooms: mapRecentSearchToRooms(recentSearch),
      source: 'recent-search',
      locationId: recentSearch.destination.id,
    };
  }

  return {
    destination: null,
    checkIn: defaults.checkIn,
    checkOut: defaults.checkOut,
    rooms: getDefaultHotelRooms(),
    source: 'default',
  };
};

export const parseHotelDeepLinkRoute = (
  url: string | null,
): HotelDeepLinkRoute => {
  let params: HotelDeepLinkParams | null = null;
  let screenPath: string | null = null;
  let isDetailsScreen = false;
  let isConfirmationScreen = false;

  if (url) {
    const parsed = parseHotelDeeplinkUrl(url);

    if (parsed) {
      const queryParams = queryParamsFromSearch(parsed.query);

      isConfirmationScreen = isConfirmationDeeplinkPath(parsed.normalizedPath);
      isDetailsScreen =
        !isConfirmationScreen &&
        Boolean(resolveDetailsExpoPath(parsed.normalizedPath));

      if (isHotelsRootOrSearchPath(parsed.normalizedPath)) {
        screenPath = shouldOpenResultsFromHotelsEntry(
          parsed.normalizedPath,
          queryParams,
        )
          ? 'results'
          : '/';
      } else {
        const path = parsed.normalizedPath ? `/${parsed.normalizedPath}` : '/';
        screenPath = normalizeScreenPath(path);
      }

      params = { ...queryParams, screenPath, isDeepLink: 'true' };
    }
  }

  const isSearchListScreen =
    !isDetailsScreen &&
    !isConfirmationScreen &&
    (screenPath === 'search' || screenPath === 'results');
  const isMapViewScreen =
    !isDetailsScreen && !isConfirmationScreen && screenPath === 'mapview';
  const isSearchWidgetScreen =
    !isSearchListScreen &&
    !isMapViewScreen &&
    !isDetailsScreen &&
    !isConfirmationScreen;

  return {
    params,
    screenPath,
    isSearchListScreen,
    isMapViewScreen,
    isSearchWidgetScreen,
    isDetailsScreen,
    isConfirmationScreen,
  };
};

export const parseHotelDeepLinkQueryParams = (
  url: string | null,
): HotelQueryParams => {
  const route = parseHotelDeepLinkRoute(url);

  if (!route.params) {
    return {};
  }

  return Object.entries(route.params).reduce<HotelQueryParams>(
    (acc, [key, value]) => {
      if (key === 'isDeepLink' || key === 'screenPath') {
        return acc;
      }

      if (typeof value === 'string' || Array.isArray(value)) {
        acc[key] = value;
      }

      return acc;
    },
    {},
  );
};

type BannerRedirectionResult =
  | { type: 'hotel-search'; queryString: string }
  | { type: 'external' };

/**
 * Resolves a banner redirectionUrl to either an in-app hotel search navigation
 * or an external browser open.
 *
 * A URL is treated as an in-app hotel search when its path matches a known
 * hotel search route (/hotel/search or /hotels/<lang>/<country>/search) AND
 * it carries at least one recognised hotel search query param.
 *
 * Everything else — promo pages, external sites, or hotel URLs without search
 * criteria — is treated as external and should be opened in the in-app browser.
 */
export const resolveBannerRedirection = (
  redirectionUrl: string,
): BannerRedirectionResult => {
  try {
    const parsed = new URL(redirectionUrl);
    const path = parsed.pathname;

    const isHotelSearchPath =
      path.includes('/hotel/search') ||
      /\/hotels\/[^/]+\/[^/]+\/search/.test(path);

    if (!isHotelSearchPath) {
      return { type: 'external' };
    }

    const queryParams = Object.fromEntries(parsed.searchParams.entries());
    if (!hasHotelSearchCriteria(queryParams)) {
      return { type: 'external' };
    }

    return { type: 'hotel-search', queryString: parsed.search };
  } catch {
    return { type: 'external' };
  }
};
